import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatError } from '@/lib/api';
import { formatClientCode } from '@/lib/clientCode';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2, Search, Eye } from 'lucide-react';

const emptyClient = { name: '', email: '', phone: '', branch_id: '', assigned_trader_id: '', kyc_status: 'pending', notes: '', status: 'active' };

const CLIENT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'stop', label: 'Stop' },
  { value: 'dormant', label: 'Dormant' },
];

const statusBadgeClass = (s) => {
  switch (s) {
    case 'active': return 'bg-brand-success/15 text-brand-success';
    case 'inactive': return 'bg-gray-200 text-gray-700';
    case 'stop': return 'bg-brand-error/15 text-brand-error';
    case 'dormant': return 'bg-amber-100 text-amber-700';
    default: return 'bg-gray-100 text-gray-600';
  }
};

export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [branches, setBranches] = useState([]);
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [search, setSearch] = useState('');
  const [statusChange, setStatusChange] = useState(null); // { client, next }
  const [statusSaving, setStatusSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [cRes, bRes, tRes] = await Promise.all([
        api.get('/clients'),
        api.get('/branches').catch(() => ({ data: [] })),
        api.get('/users', { params: { role: 'trader' } }).catch(() => ({ data: [] }))
      ]);
      setClients(cRes.data);
      setBranches(bRes.data);
      setTraders(tRes.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyClient); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, email: c.email || '', phone: c.phone || '', branch_id: c.branch_id || '', assigned_trader_id: c.assigned_trader_id || '', kyc_status: c.kyc_status || 'pending', notes: c.notes || '', status: c.status || 'active' }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/clients/${editing.id}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/clients', form);
        toast.success('Client created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted');
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const requestStatusChange = (client, next) => {
    if (!next || next === (client.status || 'active')) return;
    setStatusChange({ client, next });
  };

  const confirmStatusChange = async () => {
    if (!statusChange) return;
    setStatusSaving(true);
    try {
      await api.patch(`/clients/${statusChange.client.id}/status`, { status: statusChange.next });
      // optimistic local update
      setClients(prev => prev.map(c => c.id === statusChange.client.id ? { ...c, status: statusChange.next } : c));
      toast.success(`Status updated to ${statusChange.next}`);
      setStatusChange(null);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setStatusSaving(false);
    }
  };

  const filtered = clients.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.client_id?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.aadhaar_number?.toLowerCase().includes(q) ||
      c.legacy_client_id?.toLowerCase().includes(q)
    );
  });
  const getBranchName = (id) => branches.find(b => b.id === id)?.name || '-';
  const getTraderName = (id) => traders.find(t => t.id === id)?.name || '-';

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Clients</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Manage investor and client accounts</p>
        </div>
        <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-client-btn">
          <Plus size={16} className="mr-2" /> Add Client
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-secondary" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, account no, phone, aadhaar, email..." className="pl-9 bg-white border-brand-border rounded-sm" data-testid="client-search-input" />
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                {['ID', 'Name', 'Email', 'Phone', 'Branch', 'Trader', 'KYC', 'Main Balance', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={10} className="py-12 text-center text-brand-text-secondary">No clients found</td></tr>
                ) : filtered.map(c => {
                  const code = formatClientCode(c);
                  return (
                  <tr key={c.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 text-brand-primary font-mono text-xs font-bold" title={c.client_id}>{code}</td>
                    <td className="py-3 px-4 font-medium text-brand-text-primary">{c.name}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{c.email || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{c.phone || '-'}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{getBranchName(c.branch_id)}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{getTraderName(c.assigned_trader_id)}</td>
                    <td className="py-3 px-4">
                      <Badge className={`text-xs border-0 ${c.kyc_status === 'verified' ? 'bg-brand-success/15 text-brand-success' : c.kyc_status === 'rejected' ? 'bg-brand-error/15 text-brand-error' : 'bg-amber-100 text-amber-700'}`}>
                        {c.kyc_status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 font-semibold text-brand-text-primary" data-testid={`client-main-balance-${c.id}`}>${Number(c.main_balance ?? c.current_balance ?? 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                    <td className="py-3 px-4">
                      <Select value={c.status || 'active'} onValueChange={(v) => requestStatusChange(c, v)}>
                        <SelectTrigger
                          className={`h-8 w-[110px] rounded-sm border-0 text-xs font-medium ${statusBadgeClass(c.status)}`}
                          data-testid={`client-status-inline-${c.id}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CLIENT_STATUSES.map(s => (
                            <SelectItem key={s.value} value={s.value} data-testid={`client-status-inline-option-${c.id}-${s.value}`}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/clients/${c.id}`)} className="h-8 w-8 text-brand-primary" data-testid={`view-client-${c.id}`}><Eye size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(c)} className="h-8 w-8" data-testid={`edit-client-${c.id}`}><Pencil size={14} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} className="h-8 w-8 text-brand-error hover:text-brand-error" data-testid={`delete-client-${c.id}`}><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="client-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Client' : 'New Client'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="client-name-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="client-email-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="client-phone-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={form.branch_id} onValueChange={v => setForm({...form, branch_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="client-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assigned Trader</Label>
              <Select value={form.assigned_trader_id} onValueChange={v => setForm({...form, assigned_trader_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="client-trader-select"><SelectValue placeholder="Select trader" /></SelectTrigger>
                <SelectContent>{traders.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>KYC Status</Label>
              <Select value={form.kyc_status} onValueChange={v => setForm({...form, kyc_status: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="client-kyc-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="client-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CLIENT_STATUSES.map(s => (
                    <SelectItem key={s.value} value={s.value} data-testid={`client-status-option-${s.value}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm" data-testid="client-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="client-save-btn">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline status change confirmation */}
      <Dialog open={!!statusChange} onOpenChange={(o) => { if (!o) setStatusChange(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="status-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg">Change client status?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-brand-text-secondary py-2">
            {statusChange && (
              <>
                Change <span className="font-semibold text-brand-text-primary">{statusChange.client.name}</span> from{' '}
                <Badge className={`ml-1 mr-1 text-xs border-0 ${statusBadgeClass(statusChange.client.status)}`}>{statusChange.client.status || 'active'}</Badge>
                to
                <Badge className={`ml-1 text-xs border-0 ${statusBadgeClass(statusChange.next)}`}>{statusChange.next}</Badge>.
                {(statusChange.next === 'stop' || statusChange.next === 'dormant') && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                    This client will be excluded from Bulk P&amp;L trades and dashboard AUM totals.
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusChange(null)} className="border-brand-border rounded-sm" data-testid="status-confirm-cancel">Cancel</Button>
            <Button onClick={confirmStatusChange} disabled={statusSaving} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="status-confirm-save">
              {statusSaving ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
